/**
 * Screenshot Mode Adapter
 *
 * Provides deterministic demo data for browser-based screenshot capture.
 * Activated by setting EXPO_PUBLIC_SCREENSHOT_MODE=true.
 *
 * All screenshot-mode routing flows through this file. Screens and hooks
 * check isScreenshotMode() and use these exports to bypass Supabase queries.
 *
 * @see e2e/screenshot-setup.js
 * @see .claude/store-assets.json
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';
import * as RNLocalize from 'react-native-localize';

import type { QuizQuestion } from '../src/features/quiz-core/QuizQuestion';
import type { SinglePlayerResultsData } from '../src/navigation/types';
import type { GuestProgress } from '../src/shared/contexts/GuestUserContext';
import type {
  UserStreak,
  UserXP,
  Achievement,
} from '../src/shared/services/gamificationService';
import type { EnhancedQuestion } from '../src/types/questionFormats';

// --- Gate ---
export const isScreenshotMode = (): boolean => {
  if (process.env.EXPO_PUBLIC_SCREENSHOT_MODE === 'false') {
    return false;
  }

  const expoExtraFlag =
    Constants.expoConfig?.extra?.EXPO_PUBLIC_SCREENSHOT_MODE ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Constants as any).manifest2?.extra?.expoClient?.extra?.EXPO_PUBLIC_SCREENSHOT_MODE;

  return expoExtraFlag === 'true' || process.env.EXPO_PUBLIC_SCREENSHOT_MODE === 'true';
};

export type ScreenshotPersona = 'signed_in' | 'guest';

export const getScreenshotPersona = (): ScreenshotPersona => {
  if (typeof window !== 'undefined') {
    try {
      const storedPersona = window.sessionStorage?.getItem('kbq_screenshot_persona');
      const params = new URLSearchParams(window.location.search);
      const persona = params.get('persona');
      if (persona === 'guest') {
        window.sessionStorage?.setItem('kbq_screenshot_persona', 'guest');
        return 'guest';
      }
      if (persona === 'signed_in') {
        window.sessionStorage?.removeItem('kbq_screenshot_persona');
        return 'signed_in';
      }
      if (storedPersona === 'guest') {
        return 'guest';
      }
    } catch {
      // Ignore malformed URL state.
    }
  }

  const expoExtraPersona =
    Constants.expoConfig?.extra?.EXPO_PUBLIC_SCREENSHOT_PERSONA ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Constants as any).manifest2?.extra?.expoClient?.extra?.EXPO_PUBLIC_SCREENSHOT_PERSONA;

  return expoExtraPersona === 'guest' || process.env.EXPO_PUBLIC_SCREENSHOT_PERSONA === 'guest'
    ? 'guest'
    : 'signed_in';
};

export const isScreenshotGuestMode = (): boolean =>
  isScreenshotMode() && getScreenshotPersona() === 'guest';

// Stash `?heroq=` at MODULE LOAD, before React Navigation rewrites the address bar.
// This module is imported from App.tsx at boot, so this runs while the query string
// still exists; by the time the quiz screen builds its scenario the URL is bare, and
// reading it there silently falls back to the default — which looks exactly like the
// capture parameter being ignored.
if (typeof window !== 'undefined') {
  try {
    const initialHeroQ = new URLSearchParams(window.location.search).get('heroq');
    if (initialHeroQ) window.sessionStorage?.setItem('kbq_hero_question', initialHeroQ);
  } catch {
    // Non-fatal: the fallback question is used.
  }
}

export interface HeroQuestionSpec {
  question: string;
  options: [string, string, string, string];
  answer: string;      // must be one of options
  explanation: string;
  wrongPick?: string;  // which option to show as the wrong choice on the reveal
}

/** The listing's hero question in every UI language the app ships.
 *
 * Same fact-checked question (Joshua 6 — the walls of Jericho) in each language, using
 * that language's usual Bible book and city names. Without this, a Spanish UI capture
 * would show an English question card. Only languages the app UI supports are here:
 * showing a UI language the app does not have would misrepresent the product.
 */
const HERO_BY_LANGUAGE: Record<string, HeroQuestionSpec> = {
  es: {
    question: '¿Qué ciudad vio caer sus murallas después de que los israelitas marcharan a su alrededor durante siete días?',
    options: ['Jericó', 'Jerusalén', 'Belén', 'Hebrón'],
    answer: 'Jericó',
    explanation: 'Jericó cayó después de que Israel obedeciera las instrucciones de batalla poco habituales de Dios, en Josué 6.',
    wrongPick: 'Jerusalén',
  },
  fr: {
    question: 'Quelle ville a vu ses murailles s’effondrer après que les Israélites ont marché autour pendant sept jours ?',
    options: ['Jéricho', 'Jérusalem', 'Bethléem', 'Hébron'],
    answer: 'Jéricho',
    explanation: 'Jéricho est tombée après qu’Israël a obéi aux instructions de combat inhabituelles de Dieu, en Josué 6.',
    wrongPick: 'Jérusalem',
  },
  de: {
    question: 'Welche Stadt verlor ihre Mauern, nachdem die Israeliten sieben Tage lang um sie herumgezogen waren?',
    options: ['Jericho', 'Jerusalem', 'Bethlehem', 'Hebron'],
    answer: 'Jericho',
    explanation: 'Jericho fiel, nachdem Israel Gottes ungewöhnlichen Kampfanweisungen gehorcht hatte – Josua 6.',
    wrongPick: 'Jerusalem',
  },
  it: {
    question: 'Quale città vide crollare le sue mura dopo che gli Israeliti vi marciarono attorno per sette giorni?',
    options: ['Gerico', 'Gerusalemme', 'Betlemme', 'Ebron'],
    answer: 'Gerico',
    explanation: 'Gerico cadde dopo che Israele obbedì alle insolite istruzioni di battaglia di Dio, in Giosuè 6.',
    wrongPick: 'Gerusalemme',
  },
  pt: {
    question: 'Qual cidade teve as muralhas derrubadas depois que os israelitas marcharam ao redor dela por sete dias?',
    options: ['Jericó', 'Jerusalém', 'Belém', 'Hebrom'],
    answer: 'Jericó',
    explanation: 'Jericó caiu depois que Israel obedeceu às instruções de batalha incomuns de Deus, em Josué 6.',
    wrongPick: 'Jerusalém',
  },
  ru: {
    question: 'Стены какого города рухнули после того, как израильтяне семь дней обходили его?',
    options: ['Иерихон', 'Иерусалим', 'Вифлеем', 'Хеврон'],
    answer: 'Иерихон',
    explanation: 'Иерихон пал после того, как Израиль повиновался необычным боевым указаниям Бога (Книга Иисуса Навина, 6).',
    wrongPick: 'Иерусалим',
  },
  ko: {
    question: '이스라엘 백성이 이레 동안 성을 돈 뒤 성벽이 무너진 도시는 어디일까요?',
    options: ['여리고', '예루살렘', '베들레헴', '헤브론'],
    answer: '여리고',
    explanation: '여리고는 이스라엘이 하나님의 독특한 전투 지시에 순종한 뒤 무너졌습니다 (여호수아 6장).',
    wrongPick: '예루살렘',
  },
};

/** Marketing hero question, supplied per capture run via `?heroq=<base64 JSON>`.
 *
 * Hero videos each need a DIFFERENT real question on screen. Hardcoding a scenario
 * per video does not scale past a handful, and hardcoding marketing content into
 * app source is the wrong home for it. Reading it from the URL means the capture
 * script can shoot any verified question without this file changing again — and the
 * fallback keeps the committed default working on its own.
 *
 * Only ever fed questions that have been fact-checked against scripture; see
 * marketing/ugc/manifests/candidates.md and PLAYBOOK.md's fact-check bank.
 */
export const getHeroQuestion = (): HeroQuestionSpec => {
  const englishHero: HeroQuestionSpec = {
    question: 'Which city’s walls collapsed after the Israelites marched around them for seven days?',
    options: ['Jericho', 'Jerusalem', 'Bethlehem', 'Hebron'],
    answer: 'Jericho',
    explanation: 'Jericho fell after Israel obeyed God’s unusual battle instructions in Joshua 6.',
    wrongPick: 'Jerusalem',
  };
  const fallback: HeroQuestionSpec = HERO_BY_LANGUAGE[getScreenshotLanguage()] ?? englishHero;
  // NATIVE PATH. On iOS/Android there is no window.location, so the URL parameter
  // used by the web harness is unavailable. The question comes from the build
  // instead, via app.config.js -> extra. Native capture matters because the video
  // composites a PHONE: web fonts, spacing and safe areas differ subtly from iOS,
  // and that difference is visible once the screen is warped onto a handset.
  // Cost: one rebuild per question, versus a free URL change on web.
  const fromExtra =
    (Constants.expoConfig?.extra as Record<string, unknown> | undefined)?.EXPO_PUBLIC_HERO_QUESTION;
  if (typeof fromExtra === 'string' && fromExtra.length > 0) {
    try {
      const decoded = JSON.parse(
        typeof atob === 'function' ? atob(fromExtra) : Buffer.from(fromExtra, 'base64').toString(),
      ) as HeroQuestionSpec;
      if (decoded?.question && Array.isArray(decoded.options) && decoded.options.length === 4
          && decoded.options.includes(decoded.answer)) {
        return decoded;
      }
    } catch {
      // fall through to the web path / fallback
    }
  }

  if (typeof window === 'undefined') return fallback;
  try {
    // Persist through navigation, exactly as getScreenshotPersona does. React
    // Navigation rewrites the URL when it routes to the quiz screen, so the query
    // string is gone by the time the scenario is built — reading location.search
    // alone silently yields the fallback and the capture looks like the parameter
    // was ignored.
    const fromUrl = new URLSearchParams(window.location.search).get('heroq');
    if (fromUrl) window.sessionStorage?.setItem('kbq_hero_question', fromUrl);
    const raw = fromUrl ?? window.sessionStorage?.getItem('kbq_hero_question');
    if (!raw) return fallback;
    let json: string;
    try {
      json = decodeURIComponent(escape(window.atob(raw)));
    } catch {
      json = window.atob(raw);
    }
    const parsed = JSON.parse(json) as HeroQuestionSpec;
    if (!parsed?.question || !Array.isArray(parsed.options) || parsed.options.length !== 4) {
      return fallback;
    }
    if (!parsed.options.includes(parsed.answer)) return fallback;  // never render an unanswerable screen
    return parsed;
  } catch {
    return fallback;
  }
};

export const getScreenshotLanguage = (): string => {
  const supported = new Set(['en', 'es', 'fr', 'de', 'pt', 'ru', 'tl', 'it', 'am', 'ko']);

  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLanguage = params.get('lang');
      if (urlLanguage && supported.has(urlLanguage)) return urlLanguage;

      const settings = JSON.parse(window.localStorage?.getItem('settings') || '{}');
      if (settings.language && supported.has(settings.language)) return settings.language;

      const selectedLanguage = window.localStorage?.getItem('user_selected_language');
      if (selectedLanguage && supported.has(selectedLanguage)) return selectedLanguage;
    } catch {
      // Use fallback below.
    }
  }

  // NATIVE. There is no URL or localStorage on a device, so capture drives the language
  // through the device locale (Detox `languageAndLocale` becomes -AppleLanguages).
  // react-native-localize reads the process's preferred languages and honours that
  // launch argument; reading the AppleLanguages default through SettingsManager did not
  // (it returned English for every run). Gated on screenshot mode: production never reads this.
  if (Platform.OS !== 'web' && isScreenshotMode()) {
    try {
      const code = RNLocalize.getLocales()?.[0]?.languageCode;
      if (code && supported.has(code)) return code;
    } catch {
      // Use fallback below.
    }
  }

  return 'en';
};

// --- Demo User ---
export const DEMO_USER_ID = 'demo-user-001';
export const DEMO_USER = {
  id: DEMO_USER_ID,
  email: 'player@kingdombiblequiz.com',
  username: 'FaithfulScholar',
  display_name: 'Faithful Scholar',
  avatar_url: null,
  created_at: '2025-08-15T10:00:00Z',
};

// --- Quiz Categories ---
export const DEMO_CATEGORIES = [
  { id: 1, name: 'Old Testament', icon: '📜', question_count: 320 },
  { id: 2, name: 'New Testament', icon: '✝️', question_count: 285 },
  { id: 3, name: 'Gospels', icon: '📖', question_count: 180 },
  { id: 4, name: 'Psalms & Proverbs', icon: '🎵', question_count: 95 },
  { id: 5, name: 'Prophets', icon: '🔥', question_count: 110 },
  { id: 6, name: 'Acts & Letters', icon: '✉️', question_count: 145 },
  { id: 7, name: 'Kings & Judges', icon: '👑', question_count: 88 },
  { id: 8, name: 'Creation & Genesis', icon: '🌍', question_count: 72 },
];

const DEMO_CATEGORY_NAMES: Record<string, Record<number, string>> = {
  es: { 1: 'Antiguo Testamento', 2: 'Nuevo Testamento', 3: 'Evangelios', 4: 'Salmos y Proverbios', 5: 'Profetas', 6: 'Hechos y Cartas', 7: 'Reyes y Jueces', 8: 'Creación y Génesis' },
  fr: { 1: 'Ancien Testament', 2: 'Nouveau Testament', 3: 'Évangiles', 4: 'Psaumes et Proverbes', 5: 'Prophètes', 6: 'Actes et Lettres', 7: 'Rois et Juges', 8: 'Création et Genèse' },
  de: { 1: 'Altes Testament', 2: 'Neues Testament', 3: 'Evangelien', 4: 'Psalmen und Sprüche', 5: 'Propheten', 6: 'Apostelgeschichte und Briefe', 7: 'Könige und Richter', 8: 'Schöpfung und Genesis' },
  pt: { 1: 'Antigo Testamento', 2: 'Novo Testamento', 3: 'Evangelhos', 4: 'Salmos e Provérbios', 5: 'Profetas', 6: 'Atos e Cartas', 7: 'Reis e Juízes', 8: 'Criação e Gênesis' },
  ru: { 1: 'Ветхий Завет', 2: 'Новый Завет', 3: 'Евангелия', 4: 'Псалмы и Притчи', 5: 'Пророки', 6: 'Деяния и Послания', 7: 'Цари и Судьи', 8: 'Сотворение и Бытие' },
  tl: { 1: 'Lumang Tipan', 2: 'Bagong Tipan', 3: 'Mga Ebanghelyo', 4: 'Mga Awit at Kawikaan', 5: 'Mga Propeta', 6: 'Mga Gawa at Sulat', 7: 'Mga Hari at Hukom', 8: 'Paglikha at Genesis' },
  it: { 1: 'Antico Testamento', 2: 'Nuovo Testamento', 3: 'Vangeli', 4: 'Salmi e Proverbi', 5: 'Profeti', 6: 'Atti e Lettere', 7: 'Re e Giudici', 8: 'Creazione e Genesi' },
  am: { 1: 'ብሉይ ኪዳን', 2: 'አዲስ ኪዳን', 3: 'ወንጌሎች', 4: 'መዝሙርና ምሳሌ', 5: 'ነቢያት', 6: 'ሐዋርያትና መልእክቶች', 7: 'ነገሥታትና መሳፍንት', 8: 'ፍጥረትና ዘፍጥረት' },
  ko: { 1: '구약', 2: '신약', 3: '복음서', 4: '시편과 잠언', 5: '예언서', 6: '사도행전과 서신', 7: '왕과 사사', 8: '창조와 창세기' },
};

export const getDemoCategories = (language = getScreenshotLanguage()) =>
  DEMO_CATEGORIES.map((category) => ({
    ...category,
    name: DEMO_CATEGORY_NAMES[language]?.[category.id] ?? category.name,
  }));

// --- Demo Quiz Questions ---
export const DEMO_QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'q-001',
    question: 'How many days did God take to create the world according to Genesis?',
    options: ['5 days', '6 days', '7 days', '3 days'],
    answer: '6 days',
    explanation: 'God created the heavens and the earth in six days and rested on the seventh (Genesis 1-2:3).',
    difficulty: 'easy',
    category: 'Creation & Genesis',
    category_id: 8,
  },
  {
    id: 'q-002',
    question: 'Who was swallowed by a great fish?',
    options: ['Elijah', 'Jonah', 'Moses', 'Daniel'],
    answer: 'Jonah',
    explanation: 'Jonah was swallowed by a great fish after fleeing from God\'s command to go to Nineveh (Jonah 1:17).',
    difficulty: 'easy',
    category: 'Prophets',
    category_id: 5,
  },
  {
    id: 'q-003',
    question: 'What is the shortest verse in the Bible?',
    options: ['God is love.', 'Jesus wept.', 'Pray continually.', 'Rejoice always.'],
    answer: 'Jesus wept.',
    explanation: 'John 11:35 — "Jesus wept." is the shortest verse in most English translations.',
    difficulty: 'medium',
    category: 'Gospels',
    category_id: 3,
  },
  {
    id: 'q-004',
    question: 'Which apostle denied Jesus three times?',
    options: ['John', 'James', 'Peter', 'Thomas'],
    answer: 'Peter',
    explanation: 'Peter denied knowing Jesus three times before the rooster crowed, as Jesus predicted (Luke 22:54-62).',
    difficulty: 'easy',
    category: 'Gospels',
    category_id: 3,
  },
  {
    id: 'q-005',
    question: 'What are the Fruits of the Spirit according to Galatians 5:22-23?',
    options: [
      'Love, joy, peace, patience, kindness, goodness, faithfulness, gentleness, self-control',
      'Faith, hope, love, mercy, grace, truth, wisdom, courage, humility',
      'Righteousness, holiness, purity, obedience, reverence, devotion, praise, worship, glory',
      'Prayer, fasting, giving, service, worship, fellowship, study, witnessing, praise',
    ],
    answer: 'Love, joy, peace, patience, kindness, goodness, faithfulness, gentleness, self-control',
    explanation: 'Galatians 5:22-23 lists nine fruits of the Spirit.',
    difficulty: 'medium',
    category: 'Acts & Letters',
    category_id: 6,
  },
  {
    id: 'q-006',
    question: 'Who built the ark?',
    options: ['Abraham', 'Moses', 'Noah', 'David'],
    answer: 'Noah',
    explanation: 'God commanded Noah to build an ark to survive the great flood (Genesis 6:13-22).',
    difficulty: 'easy',
    category: 'Old Testament',
    category_id: 1,
  },
  {
    id: 'q-007',
    question: 'How many books are in the Bible?',
    options: ['66', '72', '73', '64'],
    answer: '66',
    explanation: 'The Protestant Bible contains 66 books — 39 in the Old Testament and 27 in the New Testament.',
    difficulty: 'medium',
    category: 'Old Testament',
    category_id: 1,
  },
  {
    id: 'q-008',
    question: 'In which psalm does it say "The Lord is my shepherd"?',
    options: ['Psalm 1', 'Psalm 23', 'Psalm 91', 'Psalm 119'],
    answer: 'Psalm 23',
    explanation: 'Psalm 23:1 — "The LORD is my shepherd; I shall not want."',
    difficulty: 'easy',
    category: 'Psalms & Proverbs',
    category_id: 4,
  },
  {
    id: 'q-009',
    question: 'Who was the first king of Israel?',
    options: ['David', 'Saul', 'Solomon', 'Samuel'],
    answer: 'Saul',
    explanation: 'Saul was anointed by the prophet Samuel as the first king of Israel (1 Samuel 10:1).',
    difficulty: 'medium',
    category: 'Kings & Judges',
    category_id: 7,
  },
  {
    id: 'q-010',
    question: 'What was Paul\'s name before his conversion?',
    options: ['Simon', 'Saul', 'Stephen', 'Barnabas'],
    answer: 'Saul',
    explanation: 'Saul of Tarsus was renamed Paul after his dramatic conversion on the road to Damascus (Acts 13:9).',
    difficulty: 'medium',
    category: 'Acts & Letters',
    category_id: 6,
  },
];

// --- Current quiz state (for the active quiz screen) ---
export const DEMO_CURRENT_QUESTION_INDEX = 2; // Show 3rd question
export const DEMO_QUIZ_ANSWERS = [
  { questionId: 'q-001', selectedAnswer: '6 days', correct: true, timeMs: 4200 },
  { questionId: 'q-002', selectedAnswer: 'Jonah', correct: true, timeMs: 3100 },
];

// --- Quiz Result ---
export const DEMO_QUIZ_RESULT = {
  score: 8,
  totalQuestions: 10,
  correctAnswers: 8,
  incorrectAnswers: 2,
  timeTaken: 62,
  xpEarned: 85,
  streakBonus: 15,
  category: 'Gospels',
  difficulty: 'medium' as const,
  perfectRound: false,
  newAchievements: ['badge-gospel-scholar'],
};

// --- Gamification ---
export const DEMO_USER_XP: UserXP = {
  id: 'xp-001',
  user_id: DEMO_USER_ID,
  total_xp: 4250,
  current_level: 12,
  xp_to_next_level: 150,
  rank: 'Scholar',
  quizzes_completed: 87,
  current_streak: 14,
  // The leaderboard header reads this; without it the card said "0 Achievements"
  // while the dashboard showed 8 of 8 earned.
  achievements_count: 8,
  created_at: '2025-08-15T10:00:00Z',
  updated_at: '2026-03-18T07:00:00Z',
} as UserXP;

export const DEMO_GUEST_PROGRESS: GuestProgress = {
  quizzesCompleted: 6,
  totalScore: 44,
  correctAnswers: 44,
  totalQuestions: 60,
  lastCategoryId: 4,
  categoryProgress: {
    '3': {
      quizzes: 2,
      score: 14,
      correctAnswers: 14,
      totalQuestions: 20,
    },
    '4': {
      quizzes: 3,
      score: 24,
      correctAnswers: 24,
      totalQuestions: 30,
    },
    '7': {
      quizzes: 1,
      score: 6,
      correctAnswers: 6,
      totalQuestions: 10,
    },
  },
  firstPlayedAt: '2026-03-18T07:00:00Z',
  lastPlayedAt: '2026-03-27T08:45:00Z',
  dismissedPrompts: [],
};

export const DEMO_PROFILE_PROGRESS = [
  {
    category_id: 4,
    difficulty_id: 2,
    best_score: 9,
    total_answered: 30,
    correct_answered: 24,
    completed: true,
    updated_at: '2026-03-27T08:45:00Z',
    accuracy: 80,
  },
  {
    category_id: 3,
    difficulty_id: 2,
    best_score: 8,
    total_answered: 20,
    correct_answered: 14,
    completed: true,
    updated_at: '2026-03-25T07:00:00Z',
    accuracy: 70,
  },
  {
    category_id: 7,
    difficulty_id: 1,
    best_score: 6,
    total_answered: 10,
    correct_answered: 6,
    completed: true,
    updated_at: '2026-03-20T07:00:00Z',
    accuracy: 60,
  },
];

export const DEMO_USER_STREAK: UserStreak = {
  id: 'streak-001',
  user_id: DEMO_USER_ID,
  current_streak: 14,
  longest_streak: 21,
  last_activity_date: '2026-03-18',
  grace_period_used: false,
  created_at: '2025-08-15T10:00:00Z',
  updated_at: '2026-03-18T07:00:00Z',
};

export const DEMO_ACHIEVEMENTS: Achievement[] = [
  {
    id: 'ach-001',
    achievement_key: 'first_quiz',
    name: 'First Steps',
    description: 'Complete your first quiz',
    category: 'milestone',
    icon: '🎯',
    xp_reward: 25,
    required_progress: 1,
    is_hidden: false,
    created_at: '2025-08-15T10:00:00Z',
  },
  {
    id: 'ach-002',
    achievement_key: 'streak_7',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    category: 'streak',
    icon: '🔥',
    xp_reward: 50,
    required_progress: 7,
    is_hidden: false,
    created_at: '2025-08-22T10:00:00Z',
  },
  {
    id: 'ach-003',
    achievement_key: 'streak_30',
    name: 'Fortnight of Faith',
    description: 'Maintain a 14-day streak',
    category: 'streak',
    icon: '⚡',
    xp_reward: 100,
    required_progress: 14,
    is_hidden: false,
    created_at: '2026-03-18T07:00:00Z',
  },
  {
    id: 'ach-004',
    achievement_key: 'perfect_score',
    name: 'Gospel Scholar',
    description: 'Score 100% on a Gospels quiz',
    category: 'mastery',
    icon: '📖',
    xp_reward: 75,
    required_progress: 1,
    is_hidden: false,
    created_at: '2025-10-05T10:00:00Z',
  },
  {
    id: 'ach-005',
    achievement_key: 'quiz_50',
    name: 'Half Century',
    description: 'Complete 50 quizzes',
    category: 'milestone',
    icon: '🏅',
    xp_reward: 150,
    required_progress: 50,
    is_hidden: false,
    created_at: '2026-01-20T10:00:00Z',
  },
  {
    id: 'ach-006',
    achievement_key: 'explorer_5',
    name: 'Well Rounded',
    description: 'Play a quiz in every category',
    category: 'exploration',
    icon: '🌟',
    xp_reward: 100,
    required_progress: 8,
    is_hidden: false,
    created_at: '2025-12-10T10:00:00Z',
  },
  {
    id: 'ach-007',
    achievement_key: 'lightning_judge',
    name: 'Speed of Light',
    description: 'Answer 10 questions in under 30 seconds total',
    category: 'special',
    icon: '⏱️',
    xp_reward: 75,
    required_progress: 1,
    is_hidden: false,
    created_at: '2026-02-14T10:00:00Z',
  },
  {
    id: 'ach-008',
    achievement_key: 'perfect_ten',
    name: 'Perfect Ten',
    description: 'Score 10/10 on any quiz',
    category: 'mastery',
    icon: '💯',
    xp_reward: 100,
    required_progress: 1,
    is_hidden: false,
    created_at: '2025-09-01T10:00:00Z',
  },
];

export type SmokeScenario =
  | 'mcq_single_question'
  | 'mcq_single_selected'
  | 'mcq_single_locked'
  | 'mcq_multiple_question'
  | 'mcq_multiple_selected'
  | 'mcq_multiple_locked'
  | 'true_false_question'
  | 'true_false_selected'
  | 'true_false_locked'
  | 'fill_blank_question'
  | 'fill_blank_typing'
  | 'fill_blank_locked'
  | 'ordering_question'
  | 'ordering_dragging'
  | 'ordering_locked'
  | 'mcq_multiple_review'
  | 'true_false_review'
  | 'fill_blank_review'
  | 'ordering_review'
  | 'correct_review'
  | 'correct_verse'
  | 'wrong_review'
  | 'hero_read'
  | 'hero_reveal'
  | 'hero_correct'
  | 'live_loop_auto'
  | 'live_loop_all_formats';

export interface SmokeScenarioOption {
  id: SmokeScenario;
  label: string;
  screen: 'quiz' | 'results';
}

export interface SmokeQuizState {
  questions: EnhancedQuestion[];
  currentQuestion: EnhancedQuestion;
  currentIndex: number;
  totalQuestions: number;
  selectedAnswer: string | number[] | Record<string, string> | null;
  showExplanation: boolean;
  score: number;
  timeLeft: number;
  maxTime: number;
}

export const SMOKE_SCENARIO_OPTIONS: SmokeScenarioOption[] = [
  { id: 'mcq_single_question', label: 'Smoke: MCQ Question', screen: 'quiz' },
  { id: 'mcq_single_selected', label: 'Smoke: MCQ Selected', screen: 'quiz' },
  { id: 'mcq_single_locked', label: 'Smoke: MCQ Locked', screen: 'quiz' },
  { id: 'mcq_multiple_question', label: 'Smoke: Multi Question', screen: 'quiz' },
  { id: 'mcq_multiple_selected', label: 'Smoke: Multi Selected', screen: 'quiz' },
  { id: 'mcq_multiple_locked', label: 'Smoke: Multi Locked', screen: 'quiz' },
  { id: 'true_false_question', label: 'Smoke: True False Question', screen: 'quiz' },
  { id: 'true_false_selected', label: 'Smoke: True False Selected', screen: 'quiz' },
  { id: 'true_false_locked', label: 'Smoke: True False Locked', screen: 'quiz' },
  { id: 'fill_blank_question', label: 'Smoke: Fill Blank Question', screen: 'quiz' },
  { id: 'fill_blank_typing', label: 'Smoke: Fill Blank Typing', screen: 'quiz' },
  { id: 'fill_blank_locked', label: 'Smoke: Fill Blank Locked', screen: 'quiz' },
  { id: 'ordering_question', label: 'Smoke: Ordering Question', screen: 'quiz' },
  { id: 'ordering_dragging', label: 'Smoke: Ordering Swap Preview', screen: 'quiz' },
  { id: 'ordering_locked', label: 'Smoke: Ordering Locked', screen: 'quiz' },
  { id: 'mcq_multiple_review', label: 'Smoke: MCQ Multiple', screen: 'quiz' },
  { id: 'true_false_review', label: 'Smoke: True / False', screen: 'quiz' },
  { id: 'fill_blank_review', label: 'Smoke: Fill Blank', screen: 'quiz' },
  { id: 'ordering_review', label: 'Smoke: Ordering', screen: 'quiz' },
  { id: 'correct_review', label: 'Smoke: Correct Review', screen: 'quiz' },
  { id: 'correct_verse', label: 'Smoke: Verse Reveal', screen: 'quiz' },
  { id: 'wrong_review', label: 'Smoke: Wrong Review', screen: 'quiz' },
  { id: 'hero_read', label: 'Smoke: Hero Read (marketing)', screen: 'quiz' },
  { id: 'hero_reveal', label: 'Smoke: Hero Reveal (marketing)', screen: 'quiz' },
  { id: 'hero_correct', label: 'Smoke: Hero Correct (marketing)', screen: 'quiz' },
];

const createBaseQuestion = (
  id: number,
  questionType: EnhancedQuestion['question_type'],
  question: string,
  categoryId: number,
  explanation: string,
): Pick<EnhancedQuestion, 'id' | 'question_code' | 'question_type' | 'category_id' | 'difficulty_id' | 'active' | 'question' | 'explanation'> => ({
  id,
  question_code: `SMOKE_${id}`,
  question_type: questionType,
  category_id: categoryId,
  difficulty_id: 2,
  active: true,
  question,
  explanation,
});

const withSmokePhase = <T extends EnhancedQuestion>(
  question: T,
  phase: 'entrance' | 'selected' | 'typing' | 'dragging' | 'locked' | 'feedback' | 'verse',
): T => ({
  ...question,
  format_data: {
    ...(question.format_data || {}),
    screenshot_phase: phase,
  },
});

const SMOKE_TEXT: Record<string, {
  categories: { old: string; new: string };
  mcqSingle: { question: string; options: string[]; answer: string; explanation: string };
  mcqMultiple: { question: string; options: Array<{ id: number; text: string; correct: boolean }>; explanation: string };
  trueFalse: { statement: string; explanation: string; trueText: string; falseText: string };
  fillBlank: { question: string; template: string; answer: string; typing: string; explanation: string };
  ordering: { question: string; instruction: string; items: string[]; explanation: string };
}> = {
  en: {
    categories: { old: 'Old Testament', new: 'New Testament' },
    mcqSingle: { question: 'Who walked on water to go to Jesus?', options: ['Peter', 'John', 'James', 'Andrew'], answer: 'Peter', explanation: 'Peter stepped out of the boat and walked toward Jesus on the water in Matthew 14:29.' },
    mcqMultiple: { question: 'Select ALL the fruits of the Spirit.', options: [{ id: 1, text: 'Love', correct: true }, { id: 2, text: 'Joy', correct: true }, { id: 3, text: 'Peace', correct: true }, { id: 4, text: 'Patience', correct: true }, { id: 5, text: 'Kindness', correct: true }, { id: 6, text: 'Anger', correct: false }, { id: 7, text: 'Pride', correct: false }, { id: 8, text: 'Greed', correct: false }], explanation: 'Galatians 5:22-23 names love, joy, peace, patience, kindness, goodness, faithfulness, gentleness, and self-control as fruit of the Spirit.' },
    trueFalse: { statement: 'Jesus was born in Bethlehem.', explanation: 'Luke 2 records that Joseph and Mary went to Bethlehem, where Jesus was born.', trueText: 'Yes. Scripture places Jesus’ birth in Bethlehem.', falseText: 'Not quite. Luke 2 records Jesus’ birth in Bethlehem.' },
    fillBlank: { question: 'For God so loved the ______ that he gave his one and only Son.', template: 'For God so loved the {blank1} that he gave his one and only Son.', answer: 'world', typing: 'worl', explanation: 'John 3:16 reveals God’s love for the whole world.' },
    ordering: { question: 'Arrange these events in the order they happened.', instruction: 'Arrange the events from first to last.', items: ['The Creation', 'The Exodus', 'The Flood', 'The Tower of Babel'], explanation: 'Genesis begins with Creation, then the Flood, the Tower of Babel, and much later the Exodus.' },
  },
  es: {
    categories: { old: 'Antiguo Testamento', new: 'Nuevo Testamento' },
    mcqSingle: { question: '¿Quien camino sobre el agua para ir a Jesus?', options: ['Pedro', 'Juan', 'Santiago', 'Andres'], answer: 'Pedro', explanation: 'Pedro salio de la barca y camino hacia Jesus sobre el agua en Mateo 14:29.' },
    mcqMultiple: { question: 'Selecciona TODOS los frutos del Espiritu.', options: [{ id: 1, text: 'Amor', correct: true }, { id: 2, text: 'Gozo', correct: true }, { id: 3, text: 'Paz', correct: true }, { id: 4, text: 'Paciencia', correct: true }, { id: 5, text: 'Bondad', correct: true }, { id: 6, text: 'Ira', correct: false }, { id: 7, text: 'Orgullo', correct: false }, { id: 8, text: 'Codicia', correct: false }], explanation: 'Galatas 5:22-23 nombra los frutos del Espiritu.' },
    trueFalse: { statement: 'Jesus nacio en Belen.', explanation: 'Lucas 2 registra que Jesus nacio en Belen.', trueText: 'Si. La Escritura situa el nacimiento de Jesus en Belen.', falseText: 'No exactamente. Lucas 2 registra su nacimiento en Belen.' },
    fillBlank: { question: 'Porque de tal manera amo Dios al ______ que dio a su Hijo unico.', template: 'Porque de tal manera amo Dios al {blank1} que dio a su Hijo unico.', answer: 'mundo', typing: 'mund', explanation: 'Juan 3:16 revela el amor de Dios por el mundo.' },
    ordering: { question: 'Ordena estos eventos como sucedieron.', instruction: 'Ordena los eventos del primero al ultimo.', items: ['La Creacion', 'El Exodo', 'El Diluvio', 'La Torre de Babel'], explanation: 'Genesis comienza con la Creacion, luego el Diluvio y la Torre de Babel; el Exodo ocurre despues.' },
  },
  fr: {
    categories: { old: 'Ancien Testament', new: 'Nouveau Testament' },
    mcqSingle: { question: 'Qui a marche sur l’eau pour aller vers Jesus ?', options: ['Pierre', 'Jean', 'Jacques', 'Andre'], answer: 'Pierre', explanation: 'Pierre sortit de la barque et marcha vers Jesus sur l’eau.' },
    mcqMultiple: { question: 'Selectionnez TOUS les fruits de l’Esprit.', options: [{ id: 1, text: 'Amour', correct: true }, { id: 2, text: 'Joie', correct: true }, { id: 3, text: 'Paix', correct: true }, { id: 4, text: 'Patience', correct: true }, { id: 5, text: 'Bonte', correct: true }, { id: 6, text: 'Colere', correct: false }, { id: 7, text: 'Orgueil', correct: false }, { id: 8, text: 'Avidite', correct: false }], explanation: 'Galates 5:22-23 enumere les fruits de l’Esprit.' },
    trueFalse: { statement: 'Jesus est ne a Bethleem.', explanation: 'Luc 2 rapporte que Jesus est ne a Bethleem.', trueText: 'Oui. L’Ecriture place sa naissance a Bethleem.', falseText: 'Pas tout a fait. Luc 2 rapporte sa naissance a Bethleem.' },
    fillBlank: { question: 'Car Dieu a tant aime le ______ qu’il a donne son Fils unique.', template: 'Car Dieu a tant aime le {blank1} qu’il a donne son Fils unique.', answer: 'monde', typing: 'mond', explanation: 'Jean 3:16 revele l’amour de Dieu pour le monde.' },
    ordering: { question: 'Placez ces evenements dans leur ordre.', instruction: 'Classez les evenements du premier au dernier.', items: ['La Creation', 'L’Exode', 'Le Deluge', 'La Tour de Babel'], explanation: 'La Genese commence par la Creation, puis le Deluge et Babel; l’Exode vient plus tard.' },
  },
  de: {
    categories: { old: 'Altes Testament', new: 'Neues Testament' },
    mcqSingle: { question: 'Wer ging auf dem Wasser zu Jesus?', options: ['Petrus', 'Johannes', 'Jakobus', 'Andreas'], answer: 'Petrus', explanation: 'Petrus stieg aus dem Boot und ging auf dem Wasser zu Jesus.' },
    mcqMultiple: { question: 'Waehle ALLE Fruechte des Geistes.', options: [{ id: 1, text: 'Liebe', correct: true }, { id: 2, text: 'Freude', correct: true }, { id: 3, text: 'Frieden', correct: true }, { id: 4, text: 'Geduld', correct: true }, { id: 5, text: 'Guete', correct: true }, { id: 6, text: 'Zorn', correct: false }, { id: 7, text: 'Stolz', correct: false }, { id: 8, text: 'Gier', correct: false }], explanation: 'Galater 5,22-23 nennt die Frucht des Geistes.' },
    trueFalse: { statement: 'Jesus wurde in Bethlehem geboren.', explanation: 'Lukas 2 berichtet, dass Jesus in Bethlehem geboren wurde.', trueText: 'Ja. Die Schrift nennt Bethlehem als Geburtsort Jesu.', falseText: 'Nicht ganz. Lukas 2 berichtet von Bethlehem.' },
    fillBlank: { question: 'Denn so sehr hat Gott die ______ geliebt, dass er seinen einzigen Sohn gab.', template: 'Denn so sehr hat Gott die {blank1} geliebt, dass er seinen einzigen Sohn gab.', answer: 'Welt', typing: 'Wel', explanation: 'Johannes 3,16 zeigt Gottes Liebe zur Welt.' },
    ordering: { question: 'Ordne diese Ereignisse in der richtigen Reihenfolge.', instruction: 'Ordne die Ereignisse vom ersten bis zum letzten.', items: ['Die Schoepfung', 'Der Exodus', 'Die Sintflut', 'Der Turm zu Babel'], explanation: 'Genesis beginnt mit Schoepfung, Sintflut und Babel; der Exodus kommt spaeter.' },
  },
  pt: {
    categories: { old: 'Antigo Testamento', new: 'Novo Testamento' },
    mcqSingle: { question: 'Quem andou sobre as aguas para ir ate Jesus?', options: ['Pedro', 'Joao', 'Tiago', 'Andre'], answer: 'Pedro', explanation: 'Pedro saiu do barco e caminhou sobre as aguas ate Jesus.' },
    mcqMultiple: { question: 'Selecione TODOS os frutos do Espirito.', options: [{ id: 1, text: 'Amor', correct: true }, { id: 2, text: 'Alegria', correct: true }, { id: 3, text: 'Paz', correct: true }, { id: 4, text: 'Paciencia', correct: true }, { id: 5, text: 'Bondade', correct: true }, { id: 6, text: 'Ira', correct: false }, { id: 7, text: 'Orgulho', correct: false }, { id: 8, text: 'Ganancia', correct: false }], explanation: 'Galatas 5:22-23 lista os frutos do Espirito.' },
    trueFalse: { statement: 'Jesus nasceu em Belem.', explanation: 'Lucas 2 registra que Jesus nasceu em Belem.', trueText: 'Sim. A Escritura situa o nascimento de Jesus em Belem.', falseText: 'Nao exatamente. Lucas 2 registra Belem.' },
    fillBlank: { question: 'Porque Deus amou o ______ de tal maneira que deu o seu Filho unico.', template: 'Porque Deus amou o {blank1} de tal maneira que deu o seu Filho unico.', answer: 'mundo', typing: 'mund', explanation: 'Joao 3:16 revela o amor de Deus pelo mundo.' },
    ordering: { question: 'Organize estes eventos na ordem em que aconteceram.', instruction: 'Organize do primeiro ao ultimo.', items: ['A Criacao', 'O Exodo', 'O Diluvio', 'A Torre de Babel'], explanation: 'Genesis comeca com Criacao, Diluvio e Babel; o Exodo vem depois.' },
  },
  ru: {
    categories: { old: 'Ветхий Завет', new: 'Новый Завет' },
    mcqSingle: { question: 'Кто пошел по воде к Иисусу?', options: ['Петр', 'Иоанн', 'Иаков', 'Андрей'], answer: 'Петр', explanation: 'Петр вышел из лодки и пошел по воде к Иисусу.' },
    mcqMultiple: { question: 'Выберите ВСЕ плоды Духа.', options: [{ id: 1, text: 'Любовь', correct: true }, { id: 2, text: 'Радость', correct: true }, { id: 3, text: 'Мир', correct: true }, { id: 4, text: 'Долготерпение', correct: true }, { id: 5, text: 'Доброта', correct: true }, { id: 6, text: 'Гнев', correct: false }, { id: 7, text: 'Гордость', correct: false }, { id: 8, text: 'Жадность', correct: false }], explanation: 'Галатам 5:22-23 перечисляет плод Духа.' },
    trueFalse: { statement: 'Иисус родился в Вифлееме.', explanation: 'Лука 2 говорит, что Иисус родился в Вифлееме.', trueText: 'Да. Писание указывает Вифлеем.', falseText: 'Не совсем. Лука 2 говорит о Вифлееме.' },
    fillBlank: { question: 'Ибо так возлюбил Бог ______, что отдал Сына Своего Единородного.', template: 'Ибо так возлюбил Бог {blank1}, что отдал Сына Своего Единородного.', answer: 'мир', typing: 'ми', explanation: 'Иоанна 3:16 открывает Божью любовь к миру.' },
    ordering: { question: 'Расположите эти события по порядку.', instruction: 'Поставьте события от первого к последнему.', items: ['Сотворение', 'Исход', 'Потоп', 'Вавилонская башня'], explanation: 'Бытие начинается с сотворения, потопа и Вавилона; Исход происходит позже.' },
  },
  tl: {
    categories: { old: 'Lumang Tipan', new: 'Bagong Tipan' },
    mcqSingle: { question: 'Sino ang lumakad sa tubig papunta kay Jesus?', options: ['Pedro', 'Juan', 'Santiago', 'Andres'], answer: 'Pedro', explanation: 'Lumabas si Pedro sa bangka at lumakad sa tubig papunta kay Jesus.' },
    mcqMultiple: { question: 'Piliin ang LAHAT ng bunga ng Espiritu.', options: [{ id: 1, text: 'Pag-ibig', correct: true }, { id: 2, text: 'Kagalakan', correct: true }, { id: 3, text: 'Kapayapaan', correct: true }, { id: 4, text: 'Pagtitiis', correct: true }, { id: 5, text: 'Kabutihan', correct: true }, { id: 6, text: 'Galit', correct: false }, { id: 7, text: 'Kayabangan', correct: false }, { id: 8, text: 'Kasakiman', correct: false }], explanation: 'Inililista ng Galacia 5:22-23 ang bunga ng Espiritu.' },
    trueFalse: { statement: 'Ipinanganak si Jesus sa Bethlehem.', explanation: 'Itinala ng Lucas 2 na ipinanganak si Jesus sa Bethlehem.', trueText: 'Oo. Sinasabi ng Kasulatan na sa Bethlehem Siya ipinanganak.', falseText: 'Hindi tama. Nasa Lucas 2 ang Bethlehem.' },
    fillBlank: { question: 'Sapagkat gayon na lamang ang pag-ibig ng Diyos sa ______ kaya ibinigay Niya ang Kanyang bugtong na Anak.', template: 'Sapagkat gayon na lamang ang pag-ibig ng Diyos sa {blank1} kaya ibinigay Niya ang Kanyang bugtong na Anak.', answer: 'mundo', typing: 'mund', explanation: 'Ipinapakita ng Juan 3:16 ang pag-ibig ng Diyos sa mundo.' },
    ordering: { question: 'Ayusin ang mga pangyayari ayon sa pagkakasunod.', instruction: 'Ayusin mula una hanggang huli.', items: ['Ang Paglikha', 'Ang Exodo', 'Ang Baha', 'Ang Tore ng Babel'], explanation: 'Nagsisimula ang Genesis sa Paglikha, Baha at Babel; mas huli ang Exodo.' },
  },
  it: {
    categories: { old: 'Antico Testamento', new: 'Nuovo Testamento' },
    mcqSingle: { question: 'Chi cammino sulle acque per andare da Gesu?', options: ['Pietro', 'Giovanni', 'Giacomo', 'Andrea'], answer: 'Pietro', explanation: 'Pietro scese dalla barca e cammino sulle acque verso Gesu.' },
    mcqMultiple: { question: 'Seleziona TUTTI i frutti dello Spirito.', options: [{ id: 1, text: 'Amore', correct: true }, { id: 2, text: 'Gioia', correct: true }, { id: 3, text: 'Pace', correct: true }, { id: 4, text: 'Pazienza', correct: true }, { id: 5, text: 'Bonta', correct: true }, { id: 6, text: 'Ira', correct: false }, { id: 7, text: 'Orgoglio', correct: false }, { id: 8, text: 'Avidita', correct: false }], explanation: 'Galati 5:22-23 elenca i frutti dello Spirito.' },
    trueFalse: { statement: 'Gesu nacque a Betlemme.', explanation: 'Luca 2 racconta che Gesu nacque a Betlemme.', trueText: 'Si. La Scrittura indica Betlemme.', falseText: 'Non proprio. Luca 2 parla di Betlemme.' },
    fillBlank: { question: 'Dio ha tanto amato il ______ da dare il suo unico Figlio.', template: 'Dio ha tanto amato il {blank1} da dare il suo unico Figlio.', answer: 'mondo', typing: 'mond', explanation: 'Giovanni 3:16 rivela l’amore di Dio per il mondo.' },
    ordering: { question: 'Metti questi eventi nell’ordine giusto.', instruction: 'Ordina dal primo all’ultimo.', items: ['La Creazione', 'L’Esodo', 'Il Diluvio', 'La Torre di Babele'], explanation: 'Genesi inizia con Creazione, Diluvio e Babele; l’Esodo viene dopo.' },
  },
  am: {
    categories: { old: 'ብሉይ ኪዳን', new: 'አዲስ ኪዳን' },
    mcqSingle: { question: 'ወደ ኢየሱስ ለመሄድ በውሃ ላይ የተራመደው ማን ነው?', options: ['ጴጥሮስ', 'ዮሐንስ', 'ያዕቆብ', 'እንድርያስ'], answer: 'ጴጥሮስ', explanation: 'ጴጥሮስ ከጀልባው ወጥቶ በውሃ ላይ ወደ ኢየሱስ ተጓዘ።' },
    mcqMultiple: { question: 'የመንፈስ ፍሬዎችን ሁሉ ይምረጡ።', options: [{ id: 1, text: 'ፍቅር', correct: true }, { id: 2, text: 'ደስታ', correct: true }, { id: 3, text: 'ሰላም', correct: true }, { id: 4, text: 'ትዕግሥት', correct: true }, { id: 5, text: 'ቸርነት', correct: true }, { id: 6, text: 'ቁጣ', correct: false }, { id: 7, text: 'ኩራት', correct: false }, { id: 8, text: 'ስግብግብነት', correct: false }], explanation: 'ገላትያ 5፥22-23 የመንፈስ ፍሬዎችን ይጠቅሳል።' },
    trueFalse: { statement: 'ኢየሱስ በቤተልሔም ተወለደ።', explanation: 'ሉቃስ 2 ኢየሱስ በቤተልሔም መወለዱን ይመዘግባል።', trueText: 'አዎ። ቅዱስ መጽሐፍ ቤተልሔምን ይጠቅሳል።', falseText: 'አይደለም። ሉቃስ 2 ቤተልሔምን ይጠቅሳል።' },
    fillBlank: { question: 'እግዚአብሔር ______ እንዲህ ወዶ አንድ ልጁን ሰጠ።', template: 'እግዚአብሔር {blank1} እንዲህ ወዶ አንድ ልጁን ሰጠ።', answer: 'ዓለምን', typing: 'ዓለ', explanation: 'ዮሐንስ 3፥16 የእግዚአብሔርን ፍቅር ያሳያል።' },
    ordering: { question: 'እነዚህን ክስተቶች በተከታታይ ያዘጋጁ።', instruction: 'ከመጀመሪያ እስከ መጨረሻ ያዘጋጁ።', items: ['ፍጥረት', 'ዘፀአት', 'ጥፋት ውሃ', 'የባቤል ግንብ'], explanation: 'ዘፍጥረት በፍጥረት፣ በጥፋት ውሃ እና በባቤል ይጀምራል፤ ዘፀአት በኋላ ነው።' },
  },
  ko: {
    categories: { old: '구약', new: '신약' },
    mcqSingle: { question: '예수님께 가려고 물 위를 걸은 사람은 누구입니까?', options: ['베드로', '요한', '야고보', '안드레'], answer: '베드로', explanation: '베드로는 배에서 내려 물 위를 걸어 예수님께 갔습니다.' },
    mcqMultiple: { question: '성령의 열매를 모두 고르세요.', options: [{ id: 1, text: '사랑', correct: true }, { id: 2, text: '기쁨', correct: true }, { id: 3, text: '화평', correct: true }, { id: 4, text: '오래 참음', correct: true }, { id: 5, text: '친절', correct: true }, { id: 6, text: '분노', correct: false }, { id: 7, text: '교만', correct: false }, { id: 8, text: '탐욕', correct: false }], explanation: '갈라디아서 5:22-23은 성령의 열매를 말합니다.' },
    trueFalse: { statement: '예수님은 베들레헴에서 태어나셨습니다.', explanation: '누가복음 2장은 예수님이 베들레헴에서 태어나셨다고 기록합니다.', trueText: '맞습니다. 성경은 베들레헴을 말합니다.', falseText: '아닙니다. 누가복음 2장은 베들레헴을 말합니다.' },
    fillBlank: { question: '하나님이 ______을 이처럼 사랑하사 독생자를 주셨습니다.', template: '하나님이 {blank1}을 이처럼 사랑하사 독생자를 주셨습니다.', answer: '세상', typing: '세', explanation: '요한복음 3:16은 세상을 향한 하나님의 사랑을 보여 줍니다.' },
    ordering: { question: '이 사건들을 일어난 순서대로 배열하세요.', instruction: '처음부터 마지막까지 배열하세요.', items: ['창조', '출애굽', '홍수', '바벨탑'], explanation: '창세기는 창조, 홍수, 바벨탑으로 시작하고 출애굽은 나중에 일어납니다.' },
  },
};

const getSmokeText = () => SMOKE_TEXT[getScreenshotLanguage()] ?? SMOKE_TEXT.en;

export const getAllFormatSmokeQuestions = (): EnhancedQuestion[] => [
  getSmokeQuizState('mcq_single_question').currentQuestion,
  getSmokeQuizState('mcq_multiple_question').currentQuestion,
  getSmokeQuizState('true_false_question').currentQuestion,
  getSmokeQuizState('fill_blank_question').currentQuestion,
  getSmokeQuizState('ordering_question').currentQuestion,
];

export const getSmokeQuizState = (scenario: SmokeScenario): SmokeQuizState => {
  const smokeText = getSmokeText();

  switch (scenario) {
    case 'mcq_single_question': {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9107,
          'mcq_single',
          smokeText.mcqSingle.question,
          7,
          smokeText.mcqSingle.explanation
        ),
        category: smokeText.categories.new,
        difficulty_id: 1,
        format_data: {
          question_id: 9107,
          language_code: getScreenshotLanguage(),
          option_a: smokeText.mcqSingle.options[0],
          option_b: smokeText.mcqSingle.options[1],
          option_c: smokeText.mcqSingle.options[2],
          option_d: smokeText.mcqSingle.options[3],
          correct_option: 'a',
          shuffle_options: false,
          reference: 'Matthew 14:25',
        },
        options: smokeText.mcqSingle.options,
        answer: smokeText.mcqSingle.answer,
        correct_answer: smokeText.mcqSingle.answer,
      }, 'entrance');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: null,
        showExplanation: false,
        score: 6,
        timeLeft: 12,
        maxTime: 30,
      };
    }

    case 'mcq_single_selected': {
      const base = getSmokeQuizState('mcq_single_question');
      return {
        ...base,
        currentQuestion: withSmokePhase(base.currentQuestion, 'selected'),
        questions: [withSmokePhase(base.currentQuestion, 'selected')],
        selectedAnswer: smokeText.mcqSingle.answer,
        showExplanation: false,
      };
    }

    case 'mcq_single_locked': {
      const base = getSmokeQuizState('mcq_single_question');
      return {
        ...base,
        currentQuestion: withSmokePhase(base.currentQuestion, 'locked'),
        questions: [withSmokePhase(base.currentQuestion, 'locked')],
        selectedAnswer: smokeText.mcqSingle.answer,
        showExplanation: false,
      };
    }

    case 'mcq_multiple_question': {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9108,
          'mcq_multiple',
          smokeText.mcqMultiple.question,
          3,
          smokeText.mcqMultiple.explanation
        ),
        category: smokeText.categories.new,
        difficulty_id: 2,
        format_data: {
          question_id: 9108,
          language_code: getScreenshotLanguage(),
          options: smokeText.mcqMultiple.options,
          min_selections: 5,
          max_selections: 8,
          partial_credit: true,
          reference: 'Galatians 5:22-23',
        },
      }, 'entrance');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: null,
        showExplanation: false,
        score: 8,
        timeLeft: 15,
        maxTime: 30,
      };
    }

    case 'mcq_multiple_selected': {
      const base = getSmokeQuizState('mcq_multiple_question');
      const selectedQuestion = withSmokePhase(base.currentQuestion, 'selected');
      return {
        ...base,
        questions: [selectedQuestion],
        currentQuestion: selectedQuestion,
        selectedAnswer: [1, 2, 3, 4, 5],
        showExplanation: false,
      };
    }

    case 'mcq_multiple_locked': {
      const base = getSmokeQuizState('mcq_multiple_question');
      const lockedQuestion = withSmokePhase(base.currentQuestion, 'locked');
      return {
        ...base,
        questions: [lockedQuestion],
        currentQuestion: lockedQuestion,
        selectedAnswer: [1, 2, 3, 4, 5],
        showExplanation: false,
      };
    }

    case 'true_false_question': {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9109,
          'true_false',
          smokeText.trueFalse.statement,
          3,
          smokeText.trueFalse.explanation
        ),
        category: smokeText.categories.new,
        difficulty_id: 1,
        format_data: {
          question_id: 9109,
          language_code: getScreenshotLanguage(),
          statement: smokeText.trueFalse.statement,
          correct_answer: true,
          reference: 'Luke 2:4-7',
          explanation_true: smokeText.trueFalse.trueText,
          explanation_false: smokeText.trueFalse.falseText,
        },
        answer: true,
        correct_answer: true,
      }, 'entrance');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: null,
        showExplanation: false,
        score: 5,
        timeLeft: 10,
        maxTime: 30,
      };
    }

    case 'true_false_selected': {
      const base = getSmokeQuizState('true_false_question');
      const selectedQuestion = withSmokePhase(base.currentQuestion, 'selected');
      return {
        ...base,
        questions: [selectedQuestion],
        currentQuestion: selectedQuestion,
        selectedAnswer: 'true',
        showExplanation: false,
      };
    }

    case 'true_false_locked': {
      const base = getSmokeQuizState('true_false_question');
      const lockedQuestion = withSmokePhase(base.currentQuestion, 'locked');
      return {
        ...base,
        questions: [lockedQuestion],
        currentQuestion: lockedQuestion,
        selectedAnswer: 'true',
        showExplanation: false,
      };
    }

    case 'fill_blank_question': {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9110,
          'fill_blank',
          smokeText.fillBlank.question,
          4,
          smokeText.fillBlank.explanation
        ),
        category: smokeText.categories.new,
        difficulty_id: 1,
        format_data: {
          question_id: 9110,
          language_code: getScreenshotLanguage(),
          template: smokeText.fillBlank.template,
          blanks: [
            { id: 'blank1', answer: smokeText.fillBlank.answer },
          ],
          case_sensitive: false,
          allow_partial_match: true,
          partial_credit: true,
          reference: 'John 3:16',
        },
      }, 'entrance');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: null,
        showExplanation: false,
        score: 7,
        timeLeft: 18,
        maxTime: 30,
      };
    }

    case 'fill_blank_typing': {
      const base = getSmokeQuizState('fill_blank_question');
      const typingQuestion = withSmokePhase(base.currentQuestion, 'typing');
      return {
        ...base,
        questions: [typingQuestion],
        currentQuestion: typingQuestion,
        selectedAnswer: { blank1: smokeText.fillBlank.typing },
        showExplanation: false,
        timeLeft: 9,
      };
    }

    case 'fill_blank_locked': {
      const base = getSmokeQuizState('fill_blank_question');
      const lockedQuestion = withSmokePhase(base.currentQuestion, 'locked');
      return {
        ...base,
        questions: [lockedQuestion],
        currentQuestion: lockedQuestion,
        selectedAnswer: { blank1: smokeText.fillBlank.answer },
        showExplanation: false,
        timeLeft: 4,
      };
    }

    case 'ordering_question': {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9111,
          'ordering',
          smokeText.ordering.question,
          8,
          smokeText.ordering.explanation
        ),
        category: smokeText.categories.old,
        difficulty_id: 3,
        format_data: {
          question_id: 9111,
          language_code: getScreenshotLanguage(),
          items: [
            { id: 1, content: smokeText.ordering.items[0], position: 1 },
            { id: 2, content: smokeText.ordering.items[1], position: 2 },
            { id: 3, content: smokeText.ordering.items[2], position: 3 },
            { id: 4, content: smokeText.ordering.items[3], position: 4 },
          ],
          instruction: smokeText.ordering.instruction,
          partial_credit: true,
          strict_ordering: false,
        },
      }, 'entrance');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: null,
        showExplanation: false,
        score: 9,
        timeLeft: 20,
        maxTime: 30,
      };
    }

    case 'ordering_dragging': {
      const base = getSmokeQuizState('ordering_question');
      const draggingQuestion = withSmokePhase(base.currentQuestion, 'dragging');
      return {
        ...base,
        questions: [draggingQuestion],
        currentQuestion: draggingQuestion,
        selectedAnswer: [2, 1, 3, 4],
        showExplanation: false,
      };
    }

    case 'ordering_locked': {
      const base = getSmokeQuizState('ordering_question');
      const lockedQuestion = withSmokePhase(base.currentQuestion, 'locked');
      return {
        ...base,
        questions: [lockedQuestion],
        currentQuestion: lockedQuestion,
        selectedAnswer: [2, 1, 3, 4],
        showExplanation: false,
      };
    }

    case 'mcq_multiple_review': {
      const base = getSmokeQuizState('mcq_multiple_question');
      const question = withSmokePhase(base.currentQuestion, 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: [1, 2, 3, 4, 5],
        showExplanation: true,
        score: 8,
        timeLeft: 25,
        maxTime: 30,
      };
    }

    case 'true_false_review': {
      const base = getSmokeQuizState('true_false_question');
      const question = withSmokePhase(base.currentQuestion, 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: 'false',
        showExplanation: true,
        score: 0,
        timeLeft: 25,
        maxTime: 30,
      };
    }

    case 'fill_blank_review': {
      const base = getSmokeQuizState('fill_blank_question');
      const question = withSmokePhase(base.currentQuestion, 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: { blank1: 'world' },
        showExplanation: true,
        score: 7,
        timeLeft: 25,
        maxTime: 30,
      };
    }

    case 'ordering_review': {
      const base = getSmokeQuizState('ordering_question');
      const question = withSmokePhase(base.currentQuestion, 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: [1, 2, 3, 4],
        showExplanation: true,
        score: 9,
        timeLeft: 25,
        maxTime: 30,
      };
    }

    case 'correct_review':
    case 'correct_verse': {
      const base = getSmokeQuizState('mcq_single_question');
      const question = withSmokePhase(base.currentQuestion, scenario === 'correct_verse' ? 'verse' : 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: smokeText.mcqSingle.answer,
        showExplanation: true,
        score: 6,
        timeLeft: 25,
        maxTime: 30,
      };
    }

    // Marketing hero: the SAME question as 'wrong_review', but unanswered.
    //
    // A hero video needs one coherent question across its beats — read it, then
    // reveal it. The existing scenarios could not supply that: mcq_single_question
    // and mcq_single_selected render identically (both pre-highlight an option),
    // mcq_single_locked is the "Answers Locked" interstitial rather than a reveal,
    // and wrong_review reveals a DIFFERENT question from the mcq_single set. This
    // pairs with wrong_review so a cut can go read -> reveal without the question
    // changing under the viewer.
    //
    // Jericho is used because it is strong ego-hook material and scripturally
    // clean: Jerusalem is the most famous biblical city and a very plausible wrong
    // answer, while Joshua 6 is unambiguous about which walls fell.
    case 'hero_read':
    case 'hero_reveal':
    case 'hero_correct': {
      const hero = getHeroQuestion();
      const letters = ['a', 'b', 'c', 'd'] as const;
      const answerIndex = hero.options.indexOf(hero.answer);
      const revealing = scenario !== 'hero_read';
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(9105, 'mcq_single', hero.question, 1, hero.explanation),
        format_data: {
          question_id: 9105,
          language_code: getScreenshotLanguage(),
          option_a: hero.options[0],
          option_b: hero.options[1],
          option_c: hero.options[2],
          option_d: hero.options[3],
          correct_option: letters[answerIndex >= 0 ? answerIndex : 0],
          shuffle_options: false,
        },
        options: [...hero.options],
        answer: hero.answer,
        correct_answer: hero.answer,
      } as EnhancedQuestion, revealing ? 'feedback' : 'entrance');

      // The wrong pick must not BE the answer, or the reveal shows one row both
      // red and green. Falls back to the first non-answer option.
      const wrong = hero.wrongPick && hero.wrongPick !== hero.answer
        ? hero.wrongPick
        : hero.options.find((o) => o !== hero.answer) ?? null;

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        // hero_correct is the store-listing shot: the right pick, then the explanation.
        selectedAnswer: scenario === 'hero_correct' ? hero.answer : revealing ? wrong : null,
        showExplanation: revealing,
        score: 0,
        timeLeft: 25,
        maxTime: 25,
      };
    }

    case 'wrong_review':
    default: {
      const question: EnhancedQuestion = withSmokePhase({
        ...createBaseQuestion(
          9105,
          'mcq_single',
          'Which city’s walls collapsed after the Israelites marched around them for seven days?',
          1,
          'Jericho fell after Israel obeyed God’s unusual battle instructions in Joshua 6.'
        ),
        format_data: {
          question_id: 9105,
          language_code: 'en',
          option_a: 'Jericho',
          option_b: 'Jerusalem',
          option_c: 'Bethlehem',
          option_d: 'Hebron',
          correct_option: 'a',
          shuffle_options: false,
        },
        options: ['Jericho', 'Jerusalem', 'Bethlehem', 'Hebron'],
        answer: 'Jericho',
        correct_answer: 'Jericho',
      }, 'feedback');

      return {
        questions: [question],
        currentQuestion: question,
        currentIndex: 0,
        totalQuestions: 1,
        selectedAnswer: 'Jerusalem',
        showExplanation: true,
        score: 0,
        timeLeft: 25,
        maxTime: 30,
      };
    }
  }
};

export const getSmokeResultsData = (): SinglePlayerResultsData => ({
  userId: DEMO_USER_ID,
  categoryId: 3,
  difficulty_id: 2,
  score: DEMO_QUIZ_RESULT.score,
  totalQuestions: DEMO_QUIZ_RESULT.totalQuestions,
  accuracy: Math.round((DEMO_QUIZ_RESULT.score / DEMO_QUIZ_RESULT.totalQuestions) * 100),
  answers: ['6 days', 'Jonah', 'Jesus wept.', 'Peter', 'Saul'],
  questions: [
    {
      id: 'q-001',
      question: 'How many days did God take to create the world according to Genesis?',
      options: ['5 days', '6 days', '7 days', '3 days'],
      correct_answer: '6 days',
      explanation: 'God created the heavens and the earth in six days and rested on the seventh.',
    },
    {
      id: 'q-002',
      question: 'Who was swallowed by a great fish?',
      options: ['Elijah', 'Jonah', 'Moses', 'Daniel'],
      correct_answer: 'Jonah',
      explanation: 'Jonah was swallowed by a great fish after fleeing from God’s command.',
    },
    {
      id: 'q-003',
      question: 'What is the shortest verse in the Bible?',
      options: ['God is love.', 'Jesus wept.', 'Pray continually.', 'Rejoice always.'],
      correct_answer: 'Jesus wept.',
      explanation: 'John 11:35 — “Jesus wept.” is the shortest verse in most English translations.',
    },
    {
      id: 'q-004',
      question: 'Which apostle denied Jesus three times?',
      options: ['John', 'James', 'Peter', 'Thomas'],
      correct_answer: 'Peter',
      explanation: 'Peter denied knowing Jesus three times before the rooster crowed.',
    },
    {
      id: 'q-005',
      question: 'Who was the first king of Israel?',
      options: ['David', 'Saul', 'Solomon', 'Samuel'],
      correct_answer: 'Saul',
      explanation: 'Saul was anointed by the prophet Samuel as the first king of Israel.',
    },
  ],
  correctAnswers: DEMO_QUIZ_RESULT.correctAnswers,
});

// --- Leaderboard ---
export interface LeaderboardEntry {
  user_id: string;
  username: string;
  total_xp: number;
  current_level: number;
  rank: string;
  position: number;
  avatar_url: string | null;
  quiz_count: number;
  streak_count: number;
}

export const DEMO_LEADERBOARD: LeaderboardEntry[] = [
  { user_id: 'u-001', username: 'ScriptureKing', total_xp: 12450, current_level: 28, rank: 'Sage', position: 1, avatar_url: null, quiz_count: 245, streak_count: 62 },
  { user_id: 'u-002', username: 'BibleWarrior', total_xp: 9820, current_level: 24, rank: 'Teacher', position: 2, avatar_url: null, quiz_count: 198, streak_count: 45 },
  { user_id: 'u-003', username: 'GraceAbounds', total_xp: 8340, current_level: 21, rank: 'Teacher', position: 3, avatar_url: null, quiz_count: 172, streak_count: 38 },
  { user_id: 'u-004', username: 'WordSmith', total_xp: 6750, current_level: 18, rank: 'Scholar', position: 4, avatar_url: null, quiz_count: 143, streak_count: 29 },
  { user_id: 'u-005', username: 'PsalmSinger', total_xp: 5200, current_level: 15, rank: 'Scholar', position: 5, avatar_url: null, quiz_count: 112, streak_count: 21 },
  { user_id: DEMO_USER_ID, username: 'FaithfulScholar', total_xp: 4250, current_level: 12, rank: 'Scholar', position: 6, avatar_url: null, quiz_count: 87, streak_count: 14 },
  { user_id: 'u-007', username: 'TruthSeeker', total_xp: 3900, current_level: 11, rank: 'Scholar', position: 7, avatar_url: null, quiz_count: 78, streak_count: 12 },
  { user_id: 'u-008', username: 'FaithRunner', total_xp: 3100, current_level: 10, rank: 'Scholar', position: 8, avatar_url: null, quiz_count: 64, streak_count: 9 },
  { user_id: 'u-009', username: 'HolyExplorer', total_xp: 2450, current_level: 8, rank: 'Beginner', position: 9, avatar_url: null, quiz_count: 51, streak_count: 7 },
  { user_id: 'u-010', username: 'LightBearer', total_xp: 1800, current_level: 6, rank: 'Beginner', position: 10, avatar_url: null, quiz_count: 38, streak_count: 5 },
];

// --- Streak Calendar (last 30 days) ---
export const DEMO_STREAK_DAYS: string[] = Array.from({ length: 14 }, (_, i) => {
  const d = new Date('2026-03-18T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - i);
  return d.toISOString().slice(0, 10);
});

// --- Achievements screen demo progress — pairs with DEMO_ACHIEVEMENTS (08_achievements store screenshot) ---
export const DEMO_USER_ACHIEVEMENTS = [
  { id: 'ua-001', user_id: DEMO_USER_ID, achievement_id: 'ach-001', current_progress: 1, completed: true, completed_at: '2026-02-01T10:00:00Z', created_at: '2026-02-01T10:00:00Z', updated_at: '2026-02-01T10:00:00Z' },
  { id: 'ua-002', user_id: DEMO_USER_ID, achievement_id: 'ach-002', current_progress: 7, completed: true, completed_at: '2026-02-09T10:00:00Z', created_at: '2026-02-01T10:00:00Z', updated_at: '2026-02-09T10:00:00Z' },
  { id: 'ua-003', user_id: DEMO_USER_ID, achievement_id: 'ach-003', current_progress: 14, completed: true, completed_at: '2026-03-01T10:00:00Z', created_at: '2026-02-01T10:00:00Z', updated_at: '2026-03-01T10:00:00Z' },
  { id: 'ua-004', user_id: DEMO_USER_ID, achievement_id: 'ach-004', current_progress: 1, completed: true, completed_at: '2026-03-05T10:00:00Z', created_at: '2026-02-01T10:00:00Z', updated_at: '2026-03-05T10:00:00Z' },
  { id: 'ua-005', user_id: DEMO_USER_ID, achievement_id: 'ach-005', current_progress: 38, completed: false, created_at: '2026-02-01T10:00:00Z', updated_at: '2026-03-10T10:00:00Z' },
  { id: 'ua-006', user_id: DEMO_USER_ID, achievement_id: 'ach-006', current_progress: 5, completed: false, created_at: '2026-02-01T10:00:00Z', updated_at: '2026-03-10T10:00:00Z' },
];

// --- Together hub (Family & Friends) demo data — used by GroupsHubScreen for the 04_multi store screenshot ---
export const DEMO_FAMILY = {
  id: 'demo-family-1',
  name: 'The Johnson Family',
  family_id: 'FAM-4821',
};

export const DEMO_FAMILY_MEMBERS = [
  { user_id: DEMO_USER_ID, role: 'owner', user_profiles: { name: 'Sarah', avatar_url: null } },
  { user_id: 'demo-member-2', role: 'member', user_profiles: { name: 'David', avatar_url: null } },
  { user_id: 'demo-member-3', role: 'member', user_profiles: { name: 'Grace', avatar_url: null } },
  { user_id: 'demo-member-4', role: 'member', user_profiles: { name: 'Joseph', avatar_url: null } },
];

export const DEMO_FRIENDS_GROUP = {
  id: 'demo-friends-1',
  name: 'Youth Group',
  group_code: 'YTH-7302',
};

export const DEMO_FRIENDS_MEMBERS = [
  { user_id: DEMO_USER_ID, role: 'owner', user_profiles: { name: 'Sarah', avatar_url: null } },
  { user_id: 'demo-friend-2', role: 'member', user_profiles: { name: 'Michael', avatar_url: null } },
  { user_id: 'demo-friend-3', role: 'member', user_profiles: { name: 'Ruth', avatar_url: null } },
];

// FamilyLeaderboard entry shape (src/features/groups/components/FamilyLeaderboard.tsx)
export const DEMO_FAMILY_LEADERBOARD = [
  { user_id: 'demo-member-2', username: 'David', email: '', total_points: 5820, quizzes_completed: 121, correct_answers: 968, accuracy_rate: 86, streak_days: 21, rank: 1, achievement_badges: [], family_names: ['The Johnson Family'] },
  { user_id: DEMO_USER_ID, username: 'Sarah', email: '', total_points: 4250, quizzes_completed: 87, correct_answers: 702, accuracy_rate: 84, streak_days: 14, rank: 2, achievement_badges: [], family_names: ['The Johnson Family'] },
  { user_id: 'demo-member-3', username: 'Grace', email: '', total_points: 3110, quizzes_completed: 65, correct_answers: 489, accuracy_rate: 79, streak_days: 9, rank: 3, achievement_badges: [], family_names: ['The Johnson Family'] },
  { user_id: 'demo-member-4', username: 'Joseph', email: '', total_points: 1980, quizzes_completed: 44, correct_answers: 312, accuracy_rate: 74, streak_days: 6, rank: 4, achievement_badges: [], family_names: ['The Johnson Family'] },
];

// --- Premium paywall demo products — used by useOfferings for the 09_premium store screenshot ---
// Shaped like PurchasesStoreProduct (only the fields the paywall reads).
export const DEMO_STORE_PRODUCTS = [
  {
    identifier: 'com.kingdombiblequiz.kingdombiblequiz.family.monthly',
    title: 'Family Monthly',
    description: 'Unlimited quizzes and all premium features for the whole family',
    priceString: '$4.99',
    price: 4.99,
    currencyCode: 'USD',
    subscriptionPeriod: 'P1M',
  },
  {
    identifier: 'com.kingdombiblequiz.kingdombiblequiz.family.annual',
    title: 'Family Annual',
    description: 'A full year of unlimited quizzes for the whole family',
    priceString: '$49.99',
    price: 49.99,
    currencyCode: 'USD',
    subscriptionPeriod: 'P1Y',
  },
];
